import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { ApiService } from '../core/api.service';
import { AuthService } from '../core/auth.service';

@Component({
  selector: 'app-user-create',
  imports: [FormsModule, RouterLink],
  template: `
    <section class="card">
      <h1>New User</h1>
      @if (!isAdmin()) {
        <div class="msg err">Admin access required.</div>
      } @else if (error()) { <div class="msg err">{{ error() }}</div> }
      @if (isAdmin() && created()) {
        <div class="msg ok">
          User created and ready for first Zoho sign-in.
          @if (createdPassword()) { Share the temporary local password out-of-band: <b>{{ createdPassword() }}</b> }
        </div>
        <div class="actions"><a routerLink="/users">Back to users</a></div>
      } @else if (isAdmin()) {
        <label>Username<input [(ngModel)]="username" autocomplete="username"></label>
        <label>Zoho email<input type="email" [(ngModel)]="zohoEmail" autocomplete="email"></label>
        <label>Role<select [(ngModel)]="role"><option value="user">user</option><option value="admin">admin</option></select></label>
        @if (isLocalAuth()) {
          <label>Temporary password (optional)<input type="password" [(ngModel)]="tempPassword"></label>
          <div class="hint">Leave blank to create a Zoho-only user. If supplied, policy is 12+ chars with at least 3 of upper/lower/digit/symbol.</div>
        }
        <label class="inline"><input type="checkbox" [(ngModel)]="isActive"> Active</label>
        <div class="hint">The user’s first verified Zoho sign-in will permanently link this email to their Zoho identity.</div>
        <div class="actions">
          <button type="button" (click)="save()" [disabled]="busy()">{{ busy() ? 'Creating…' : 'Create User' }}</button>
          <a routerLink="/users">Cancel</a>
        </div>
      }
    </section>
  `,
  styles: `
    .card{max-width:520px;background:#fff;border:1px solid var(--line);border-radius:16px;padding:24px;display:grid;gap:12px}
    h1{margin:0}
    label{display:grid;gap:6px;font-weight:600}
    .inline{display:flex;align-items:center;gap:8px}
    input,select{height:42px;border:1px solid #c8d2e1;border-radius:10px;padding:0 12px}
    .msg{padding:10px 12px;border-radius:10px}
    .err{background:#fdeaea;color:#a40000}
    .ok{background:#eaf7ef;color:#0b5}
    .hint{font-size:12px;color:var(--muted)}
    .actions{display:flex;gap:12px;align-items:center}
    button{height:40px;border:0;border-radius:10px;background:var(--primary);color:#fff;font-weight:700;padding:0 14px;cursor:pointer}
    a{color:var(--primary);text-decoration:none}
  `,
})
export class UserCreateComponent {
  private readonly api = inject(ApiService);
  private readonly auth = inject(AuthService);
  readonly busy = signal(false);
  readonly error = signal('');
  readonly created = signal(false);
  readonly createdPassword = signal('');
  readonly isAdmin = signal(String(this.auth.user()?.role ?? '').toLowerCase() === 'admin');
  readonly isLocalAuth = computed(() => this.auth.authenticationMode() !== 'zoho');
  username = '';
  zohoEmail = '';
  role: 'admin' | 'user' = 'user';
  tempPassword = '';
  isActive = true;

  async save(): Promise<void> {
    if (!this.isAdmin()) {
      this.error.set('Admin access required.');
      return;
    }
    this.error.set('');
    this.busy.set(true);
    try {
      const result = await firstValueFrom(
        this.api.post('/users', { username: this.username, zohoEmail: this.zohoEmail, tempPassword: this.tempPassword || undefined, role: this.role, isActive: this.isActive }),
      );
      this.createdPassword.set(String((result as { tempPassword?: string | null }).tempPassword ?? ''));
      this.created.set(true);
    } catch {
      this.error.set('Create user failed.');
    } finally {
      this.busy.set(false);
    }
  }
}
